/**
 * @module cdoEntryHumanizer
 *
 * Rewrites the numeric enum/union tags in a CDO entry's Object API tree as
 * readable names, for display only. `{ "messageType": 3 }` becomes
 * `{ "messageType": "Usage_UsageMessage (3)" }`, so an operator can read what a
 * mapping row sends while the number that is actually written stays visible.
 *
 * The tree is plain nested objects with no type information left on it, so each
 * tag is resolved by where it sits, mirroring how the LN2 message is nested
 * (`msg.content` -> category message -> device wrapper -> variant -> leaf):
 *   - Fixed fields (`idType`, `attributeId`, `valueType`, `contentType`) map to one enum.
 *   - `msg.content.messageType` is the `MessageContent` category.
 *   - Below it, a `<x>Type` / `messageType` tag next to a payload is the *variant*
 *     (Normal/Valued) when that payload is the leaf (it holds the enum `message`),
 *     and the *device* (Elevator/Escalator) otherwise.
 *   - The leaf `message` number is the row's own message, named from its composite key.
 * A tag that none of this resolves is left as the bare number rather than guessed.
 */
import { AttributeId } from "../generated/opc-ua/attribute-id";
import { NodeIdentifier } from "../generated/opc-ua/node-identifier";
import { Value } from "../generated/value/value";
import { EncodedMessageContent } from "../generated/ln2/encoded-message-content";
import { MessageContent } from "../generated/ln2/message-content";
import { DeviceStatusContent } from "../generated/status/device-status-content";
import { StatusVariant } from "../generated/status/status-variant";
import { DeviceTrafficMessage } from "../generated/traffic/device-traffic-message";
import { TrafficVariant } from "../generated/traffic/traffic-variant";
import { DeviceUsageContent } from "../generated/usage/device-usage-content";
import { UsageVariant } from "../generated/usage/usage-variant";
import { DeviceServiceContent } from "../generated/service/device-service-content";
import { ServiceVariant } from "../generated/service/service-variant";
import { DeviceControlMessage } from "../generated/control/device-control-message";
import { ControlVariant } from "../generated/control/control-variant";
import { FaultContent } from "../generated/fault/fault-content";
import { FaultVariant } from "../generated/fault/fault-variant";
import { DeviceGroupSupervisoryStatusContent } from "../generated/group-supervisory-status/device-group-supervisory-status-content";
import { GroupSupervisoryStatusVariant } from "../generated/group-supervisory-status/group-supervisory-status-variant";
import { DeviceGroupControlContent } from "../generated/group-control/device-group-control-content";
import { GroupControlVariant } from "../generated/group-control/group-control-variant";
import { DeviceControllerInfoContent } from "../generated/controller-info/device-controller-info-content";
import { ControllerInfoVariant } from "../generated/controller-info/controller-info-variant";
import { getDisplayName, messageContentToCategoryKey } from "../types/ln2MessageOptions";

/** A TypeScript numeric enum object; its reverse mapping gives the name. */
type NumericEnum = Record<number, string>;

interface CategoryEnums {
  device: NumericEnum;
  variant: NumericEnum;
}

const CATEGORY_ENUMS: Record<string, CategoryEnums> = {
  status: { device: DeviceStatusContent, variant: StatusVariant },
  traffic: { device: DeviceTrafficMessage, variant: TrafficVariant },
  usage: { device: DeviceUsageContent, variant: UsageVariant },
  service: { device: DeviceServiceContent, variant: ServiceVariant },
  control: { device: DeviceControlMessage, variant: ControlVariant },
  fault: { device: FaultContent, variant: FaultVariant },
  group_supervisory: {
    device: DeviceGroupSupervisoryStatusContent,
    variant: GroupSupervisoryStatusVariant,
  },
  group_control: { device: DeviceGroupControlContent, variant: GroupControlVariant },
  controller_info: { device: DeviceControllerInfoContent, variant: ControllerInfoVariant },
};

const FIXED_ENUMS: Record<string, NumericEnum> = {
  idType: NodeIdentifier,
  attributeId: AttributeId,
  valueType: Value,
  contentType: EncodedMessageContent,
};

type Json = Record<string, unknown>;

function isObject(v: unknown): v is Json {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

function label(names: NumericEnum | undefined, n: number, override?: string): string | number {
  const name = override ?? names?.[n];
  return name === undefined ? n : `${name} (${n})`;
}

/** The payload key a tag introduces: `statusType` -> `status`, `messageType` -> `message`. */
function payloadKey(tagKey: string): string {
  return tagKey === "messageType" ? "message" : tagKey.replace(/Type$/, "");
}

interface Ctx {
  /** Category enums once inside `msg.content.message`; undefined above that. */
  category: CategoryEnums | undefined;
  /** Name of the row's own message, for the leaf `message` number. */
  messageName: string;
}

function walk(value: unknown, ctx: Ctx, parentKey: string): unknown {
  if (Array.isArray(value)) return value.map((v) => walk(v, ctx, parentKey));
  if (!isObject(value)) return value;

  // `msg.content` is where the MessageContent category tag lives; everything
  // under its `message` belongs to that category.
  const isContent = parentKey === "content" && "messageType" in value && "message" in value;
  let childCtx = ctx;
  if (isContent && typeof value.messageType === "number") {
    const categoryKey = messageContentToCategoryKey(value.messageType as MessageContent);
    childCtx = { ...ctx, category: categoryKey ? CATEGORY_ENUMS[categoryKey] : undefined };
  }

  const out: Json = {};
  Object.entries(value).forEach(([key, v]) => {
    if (typeof v === "number") {
      if (isContent && key === "messageType") {
        out[key] = label(MessageContent as NumericEnum, v);
        return;
      }
      if (key in FIXED_ENUMS) {
        out[key] = label(FIXED_ENUMS[key], v);
        return;
      }
      if (ctx.category !== undefined) {
        // The enum `message` of the leaf is the row's own message.
        if (key === "message") {
          out[key] = label(undefined, v, ctx.messageName);
          return;
        }
        if (key === "messageType" || key.endsWith("Type")) {
          const payload = value[payloadKey(key)];
          const isLeaf = isObject(payload) && typeof payload.message === "number";
          out[key] = label(isLeaf ? ctx.category.variant : ctx.category.device, v);
          return;
        }
      }
    }
    out[key] = walk(v, key === "message" && isContent ? childCtx : ctx, key);
  });
  return out;
}

/**
 * Display copy of a CDO entry (from `opcUaMappingRowToCdoEntry`, already
 * JSON-safe) with its enum tags named. `msgId` is the row's composite key,
 * used to name the leaf message.
 */
export default function humanizeCdoEntry(entry: unknown, msgId: string): unknown {
  const full = getDisplayName(msgId);
  // "Usage: Front Door Opening" -> "Front Door Opening"
  const messageName = full.includes(": ") ? full.slice(full.indexOf(": ") + 2) : full;
  return walk(entry, { category: undefined, messageName }, "");
}
